import React from 'react';

import {
  actions,
  BonusesHistoryProvider,
  DateService,
  SiteConfigManager,
  useBonusesHistory,
  useBonusesHistoryDispatch,
} from '@atlas/core';
import { EmptyStateView, Loader, Pagination } from '@atlas/ui';

import TransactionHistoryTableItem from 'components/ClientProfile/Bonuses/BonusesHistory/BonusesHistoryTableItem';
import SeoPageText from 'components/SeoPage/SeoPageText';

import { bonusTransactionHistoryFilter } from './BonusesHistoryFilter';

export default function ConnectedBonusesHistory() {
  const options = {
    filter: {
      types: bonusTransactionHistoryFilter,
    },
  };

  return (
    <BonusesHistoryProvider options={options}>
      <TransactionHistoryView />
    </BonusesHistoryProvider>
  );
}

const TransactionHistoryView = () => {
  const { filter, loading, paging, transactions } = useBonusesHistory();

  const dispatch = useBonusesHistoryDispatch();

  const changePageHandler = React.useCallback(
    nextPage => {
      dispatch({
        payload: { currentPage: nextPage },
        type: actions.FILTER_UPDATE,
      });
    },
    [dispatch],
  );

  const updateTransactionsAfterCancel = React.useCallback(
    id => {
      dispatch({
        payload: { filter: { id } },
        type: actions.FILTER_UPDATE,
      });
    },
    [dispatch],
  );

  if (loading) {
    return <Loader className="relative" />;
  }

  if (transactions.length === 0) {
    return (
      <EmptyStateView
        className="empty-center"
        description="bonus_history_empty_message"
        title="bonus_history_empty_title"
      />
    );
  }

  const dateFormat = SiteConfigManager.getConfig('config.config.date.dateFormat');
  const transactionDatesFormat = dateFormat === 'MM.DD' ? 'MMM DD YYYY' : 'DD MMM YYYY';
  const transactionDates = transactions.reduce(
    (acc, transaction, index) => {
      const formattedDate = DateService.getFormattedDate(
        transaction.creationDate,
        transactionDatesFormat,
      );

      if (acc.prevDataIndex === null) {
        return {
          data: { 0: formattedDate },
          prevDataIndex: 0,
        };
      }

      if (acc.data[acc.prevDataIndex] !== formattedDate) {
        return {
          data: {
            ...acc.data,
            [index]: formattedDate,
          },
          prevDataIndex: index,
        };
      }

      return acc;
    },
    {
      data: {},
      prevDataIndex: null,
    },
  );

  return (
    <div className="transaction-history-list">
      {transactions.map((transaction, index) => (
        <>
          {!!transactionDates.data[index] && (
            <div className="transaction-history-list-date">{transactionDates.data[index]}</div>
          )}
          <TransactionHistoryTableItem
            currentPage={filter.currentPage}
            key={transaction.id}
            transaction={transaction}
            updateTransactionsAfterCancel={updateTransactionsAfterCancel}
          />
        </>
      ))}

      {paging && (
        <Pagination
          changeCurrentPage={changePageHandler}
          currentPage={filter.currentPage}
          pages={paging.totalPages}
          siblingCount={0}
        />
      )}

      <SeoPageText />
    </div>
  );
};
